import cn from 'classnames';
import * as React from 'react';

import Text from '../text';
import type { ValueOf } from '../indexed-access-type';
import { themes as textThemes, sizes as textSizes } from '../text/text.types';

import {
  sizes,
  themes,
  variants,
  VisuallyButton as Props,
} from './visually-button.types';

const italicMap: Partial<Record<ValueOf<typeof variants>, true>> = {
  cta: true,
  'cta-secondary': true,
  'cta-secondary-white': true,
};

const sizeMap: Record<keyof typeof sizes, keyof typeof textSizes> = {
  small: textSizes.small,
  basic: textSizes.basic,
  large: textSizes.large,
};

const themeMap: Record<ValueOf<typeof variants>, keyof typeof textThemes> = {
  complete: textThemes.emphasis,
  cta: textThemes.headline,
  'cta-secondary': textThemes.headline,
  'cta-secondary-white': textThemes.headline,
  primary: textThemes.emphasis,
  'primary-destructive': textThemes.emphasis,
  'primary-white': textThemes.emphasis,
  secondary: textThemes.emphasis,
  'secondary-white': textThemes.emphasis,
  'secondary-destructive': textThemes.emphasis,
  tertiary: textThemes.emphasis,
  'tertiary-destructive': textThemes.emphasis,
  'fixed-tertiary': textThemes.emphasis,
  waitlist: textThemes.emphasis,
  'waitlist-secondary': textThemes.emphasis,
};

const VisuallyButton: React.FunctionComponent<Props> & {
  sizes: typeof sizes;
  variants: typeof variants;
  themes: typeof themes;
} = ({
  ariaLabel,
  className,
  elementName = 'button',
  leadingIcon,
  trailingIcon,
  hasStackedIcon,
  size = sizes.basic,
  testId,
  text,
  theme = themes.normal,
  variant = variants.primary,
  wide,
  ...rest
}) =>
  React.createElement(
    elementName,
    {
      'aria-label': leadingIcon && ariaLabel ? ariaLabel : undefined,
      className: cn(
        'visually-button',
        {
          'visually-button--icon': leadingIcon && !text,
          'visually-button--icon-text': (leadingIcon || trailingIcon) && text,
          'visually-button--icon-text--stacked': hasStackedIcon,
          [`visually-button--size-${size}`]: size,
          [`visually-button--variant-${variant}`]: variant,
          'visually-button--wide': wide,
          [`visually-button--theme-${theme}`]: theme
            ? themes[theme]
            : undefined,
        },
        className,
      ),
      'data-testid': testId,
      ...rest,
    },
    <React.Fragment>
      {leadingIcon ? (
        <div className="visually-button__icon">{leadingIcon}</div>
      ) : null}
      <Text
        className="visually-button__text"
        italic={italicMap[variant]}
        size={sizeMap[size]}
        theme={themeMap[variant]}
        elementName="span"
      >
        {text}
      </Text>
      {trailingIcon ? (
        <div className="visually-button__icon">{trailingIcon}</div>
      ) : null}
      {theme === 'spinner' && (
        <div className="visually-button__spinner">
          <svg
            className="visually-button__spinner-icon"
            width="24px"
            height="24px"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle opacity="0.1" cx="12" cy="12" r="10" />
            <path
              d="M12 2C13.9778 2 15.9112 2.58649 17.5557 3.6853C19.2002 4.78412 20.4819 6.3459 21.2388 8.17316C21.9957 10.0004 22.1937 12.0111 21.8079 13.9509C21.422 15.8907 20.4696 17.6725 19.0711 19.0711C17.6725 20.4696 15.8907 21.422 13.9509 21.8079C12.0111 22.1937 10.0004 21.9957 8.17317 21.2388C6.3459 20.4819 4.78412 19.2002 3.6853 17.5557C2.58649 15.9112 2 13.9778 2 12"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      )}
    </React.Fragment>,
  );

VisuallyButton.sizes = sizes;
VisuallyButton.variants = variants;
VisuallyButton.themes = themes;

export default VisuallyButton;
